// ============================================================================
// STUDIO CANETTE — APPELS À L'API
//
// Toute communication avec le serveur Express passe par ici. Trois raisons :
//
//  1. Le jeton. Le Bearer doit être *frais* : on le relit à chaque appel dans
//     `pb.authStore`, que session.js maintient à jour (renouvellement au
//     démarrage puis toutes les trente minutes). Mémoriser le jeton une fois
//     pour toutes produirait des 401 aléatoires au bout d'une heure de travail
//     dans le studio.
//  2. Le temps. /api/generate appelle un modèle d'image : 120 s de patience,
//     là où /api/me doit rendre la main en quelques secondes. Un fetch sans
//     AbortController laisse une requête pendre indéfiniment.
//  3. Les codes métier. Un 402 n'est pas une panne, c'est une proposition
//     commerciale — la page appelante doit pouvoir l'afficher elle-même. Un 401
//     en revanche est terminal : on purge et on renvoie vers la connexion.
// ============================================================================

import { pb, hasPB } from './pb.js';
import { ERRORS } from '../../shared/copy.js';

const BASE = '/api';
const TIMEOUT_DEFAULT = 30_000;
// 240 s, et ce n'est pas de la marge de confort : GPT Image 2 met environ
// 130 secondes sur une reference 3D, mesure chronometre en main. A 120 s le
// navigateur abandonnait une generation que le serveur menait pourtant a bien
// — l'utilisateur voyait une erreur alors que son image etait deja archivee et
// son credit legitimement depense. Cette valeur doit rester SUPERIEURE a
// IMAGE_TIMEOUT_MS cote serveur, sinon on coupe le seul qui sait rembourser.
const TIMEOUT_SLOW = 240_000;

/**
 * Routes lentes par nature : le modèle travaille, on attend.
 * `/assist` en fait partie — il regarde une maquette et rédige un correctif,
 * ce qui prend une dizaine de secondes, parfois trois fois plus.
 */
const SLOW = /^\/(generate|assist)\b/;

/** Chemins d'authentification : y renvoyer un 401 tournerait en boucle. */
const AUTH_PATHS = /^\/(connexion|inscription|mot-de-passe-oublie|nouveau-mdp)\b/;

/**
 * Erreur d'API. `code` est le champ `error` du corps JSON (`insufficient_credits`,
 * `feature_locked`, `rate_limited`…), la seule chose sur laquelle le front doit
 * brancher sa logique — jamais sur le texte du message.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, code = null, payload = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

/** Messages de repli quand le serveur n'a rien dit d'intelligible. */
function fallbackMessage(status, code) {
  if (code && ERRORS[code]) return ERRORS[code];
  if (status === 401) return ERRORS.auth_required;
  if (status === 403) return ERRORS.feature_locked;
  if (status === 404) return 'Ressource introuvable.';
  if (status === 413) return 'Fichier trop lourd pour être envoyé.';
  if (status === 429) return ERRORS.rate_limited;
  if (status >= 500) return "Le serveur n'a pas pu traiter la demande.";
  return 'Requête refusée.';
}

async function bearer() {
  if (!hasPB) return null;
  try {
    // PocketBase garde le jeton en clair dans son authStore (localStorage) et
    // ne le renouvelle pas de lui-même : c'est session.js qui s'en charge.
    // Ici on se contente de lire l'état courant — pas d'appel réseau sur le
    // chemin critique de chaque requête.
    return pb.authStore?.isValid ? pb.authStore.token : null;
  } catch {
    return null;
  }
}

// Une page qui lance trois appels en parallèle peut recevoir trois 401 :
// on ne purge et ne redirige qu'une fois.
let purging = false;

async function handleUnauthorized() {
  if (purging) return;
  purging = true;
  // Purge purement locale : le jeton est déjà refusé par le serveur, inutile
  // d'aller le lui redemander. `clear()` vide le store et déclenche l'écouteur
  // de session.js, qui remet l'interface en état déconnecté.
  try { pb.authStore?.clear(); } catch { /* déjà déconnecté */ }

  if (AUTH_PATHS.test(location.pathname)) { purging = false; return; }
  const suite = location.pathname + location.search + location.hash;
  const to = '/connexion?suite=' + encodeURIComponent(suite);
  try {
    // Import différé : le routeur importe la couche API par la bande (pages,
    // session…). Un import statique créerait un cycle au chargement.
    const { navigate } = await import('./router.js');
    navigate(to, { replace: true });
  } catch {
    location.assign(to);
  } finally {
    setTimeout(() => { purging = false; }, 1500);
  }
}

/**
 * Appelle l'API.
 *
 * @param {string} path                 '/me', '/generate', '/renders?limit=20'
 * @param {Object} [opts]
 * @param {string} [opts.method]
 * @param {*}      [opts.body]          objet → JSON ; FormData → tel quel
 * @param {AbortSignal} [opts.signal]   annulation par l'appelant
 * @param {number} [opts.timeout]       ms ; 120 000 par défaut sur /generate
 * @returns {Promise<*>} le corps JSON, ou null sur 204
 * @throws {ApiError}
 */
export async function api(path, { method = 'GET', body, signal, timeout, headers } = {}) {
  // On accepte '/me', 'me' et '/api/me' : les appelants ne doivent pas avoir à
  // se souvenir du préfixe.
  let p = String(path || '');
  if (p.startsWith(BASE + '/')) p = p.slice(BASE.length);
  if (!p.startsWith('/')) p = '/' + p;

  const ms = timeout ?? (SLOW.test(p) ? TIMEOUT_SLOW : TIMEOUT_DEFAULT);
  const ctl = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => { timedOut = true; ctl.abort(); }, ms);
  const relay = () => ctl.abort();
  if (signal) {
    if (signal.aborted) { clearTimeout(timer); throw new ApiError('Requête annulée.', { code: 'aborted' }); }
    signal.addEventListener('abort', relay, { once: true });
  }

  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const init = {
    method,
    signal: ctl.signal,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(headers || {}) }
  };
  if (body !== undefined && body !== null) {
    if (isForm) init.body = body;                       // le navigateur pose le boundary
    else { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(body); }
  }

  const token = await bearer();
  if (token) init.headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(BASE + p, init);
  } catch (e) {
    if (timedOut) throw new ApiError("Le serveur met trop de temps à répondre. Rien n'a été facturé.", { code: 'timeout' });
    if (signal?.aborted || e?.name === 'AbortError') throw new ApiError('Requête annulée.', { code: 'aborted' });
    throw new ApiError(ERRORS.network, { code: 'network', payload: { cause: String(e?.message || e) } });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', relay);
  }

  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  let data = null;
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    const text = await res.text().catch(() => '');
    data = text ? { message: text.slice(0, 400) } : null;
  }

  if (res.ok) return data;

  const code = typeof data?.error === 'string' ? data.error : null;
  const err = new ApiError(data?.message || fallbackMessage(res.status, code), {
    status: res.status, code, payload: data
  });

  // 401 : la session est morte, on nettoie et on renvoie vers /connexion.
  // 402 : surtout PAS de redirection — la page doit proposer un rechargement de
  // crédits à l'endroit exact où l'utilisateur travaillait.
  if (res.status === 401) await handleUnauthorized();

  throw err;
}
